import React, { CSSProperties, SyntheticEvent, useEffect, useMemo } from "react";

import {
  DropdownProps,
  Icon,
  Input,
  InputProps,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "semantic-ui-react";

import moment from "moment";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

import SnackMessage from "react-lib/apps/common/SnackMessage";

import CardEmployeeSummaryWorkHourUX from "./CardEmployeeSummaryWorkHourUX";

import {
  ACTIONS,
  BTN_ACTS,
  PickedState,
  RunSequence,
  SEQUENCE_NAME,
  SetProp,
  State,
} from "./sequence/EmployeeSWH";

import "./TableEmployee.scss";
import { formatISOToThaiDate } from "react-lib/utils/dateUtils";

// Types
type CardEmployeeSummaryWorkHourProps = Pick<PickedState, "buttonLoadCheck" | "errorMessage"> & {
  setProp: SetProp;
  runSequence: RunSequence;
  EmployeeSWHSequence: State["EmployeeSWHSequence"];
  style?: CSSProperties;
  readOnly?: boolean;
  languageUX: "en" | "th";
  divisionOptions: Record<string, any>[];
};

const CardEmployeeSummaryWorkHour = (props: CardEmployeeSummaryWorkHourProps) => {
  useEffect(() => {
    props.runSequence({ sequence: "EmployeeSWH", restart: true });
  }, []);

  const lastUpdated = useMemo(() => {
    const lastApproved = props.EmployeeSWHSequence?.last_approved;
    const lastSendPayroll = props.EmployeeSWHSequence?.last_send_payroll;

    const formatDate = (date: string) =>
      date ? moment(date).format("DD/MM/YYYY [[]HH:mm[]]") : "";

    return {
      approved: formatDate(lastApproved || ""),
      sent: formatDate(lastSendPayroll || ""),
    };
  }, [props.EmployeeSWHSequence?.last_approved, props.EmployeeSWHSequence?.last_send_payroll]);

  const formattedItems = useMemo(
    () =>
      (props.EmployeeSWHSequence?.items ?? []).map((item) => {
        const hasAllOvertimeData = item.ot_1hr !== "" && item.ot_1_5hr !== "" && item.ot_3hr !== "";
        const totalOT = Number(item.ot_1hr) + Number(item.ot_1_5hr) + Number(item.ot_3hr);
        const isMismatch = item.sum_ot !== totalOT || !hasAllOvertimeData;
        const totalSlot = Object.values({
          ...item.template_summary,
          ...item.manual_summary,
        }).reduce((result, value) => result + value, 0);

        return { ...item, isMismatch, totalSlot };
      }),
    [props.EmployeeSWHSequence?.items]
  );

  const enabledSend = useMemo(() => {
    const nonPayrollItems = formattedItems.filter((item) => !item.isPayroll);

    return nonPayrollItems.length > 0 && !nonPayrollItems.every((item) => item.isMismatch);
  }, [formattedItems]);

  const enabledSaveDraft = useMemo(() => {
    const nonPayrollItems = formattedItems.filter((item) => !item.isPayroll);

    return (
      nonPayrollItems.length > 0 &&
      nonPayrollItems.every(
        (item) => item.ot_1hr !== "" && item.ot_1_5hr !== "" && item.ot_3hr !== ""
      )
    );
  }, [formattedItems]);

  const handleChangeFilter = (e: SyntheticEvent | null, data: Record<string, any>) => {
    const name = data.name;

    props.setProp("EmployeeSWHSequence.filter", {
      ...props.EmployeeSWHSequence?.filter,
      [name]: data.value,
    });
  };

  const handleSearch = () => {
    props.runSequence({ sequence: "EmployeeSWH", action: ACTIONS.SEARCH });
  };

  const handleChangeDivision = (e: SyntheticEvent, data: DropdownProps) => {
    props.runSequence({
      sequence: "EmployeeSWH",
      action: ACTIONS.CHANGE_DIVISION,
      value: Number(data.value),
    });
  };

  const handleChangeValue = (index: number) => (e: SyntheticEvent, data: InputProps) => {
    const items: any[] = props.EmployeeSWHSequence?.items ?? [];

    items[index][data.name] = data.value;

    props.setProp("EmployeeSWHSequence.items", [...items]);
  };

  const handleCalculate = () => {
    props.runSequence({ sequence: "EmployeeSWH", action: ACTIONS.CALCULATE });
  };

  const handleSendPayroll = () => {
    props.runSequence({
      sequence: "EmployeeSWH",
      action: ACTIONS.SEND_PAYROLL,
      items: formattedItems.filter((item) => !item.isMismatch && !item.isPayroll),
    });
  };

  const handleSaveDraft = () => {
    props.runSequence({
      sequence: "EmployeeSWH",
      action: ACTIONS.SAVE_DRAFT,
      items: formattedItems.filter((item) => !item.isPayroll),
    });
  };

  const handlePrintReport = () => {
    props.runSequence({ sequence: "EmployeeSWH", action: ACTIONS.PRINT_REPORT });
  };

  console.log("CardEmployeeSummaryWorkHour props:", props);

  return (
    <div style={{ ...props.style }}>
      <SnackMessage
        error={props.errorMessage?.[SEQUENCE_NAME]}
        success={null}
        onClose={() => {
          props.setProp(`errorMessage.${SEQUENCE_NAME}`, null);
        }}
      />

      <CardEmployeeSummaryWorkHourUX
        filter={props.EmployeeSWHSequence?.filter}
        isLoadingDivision={!!props.buttonLoadCheck?.[BTN_ACTS.CHANGE_DIVISION]}
        lastUpdated={lastUpdated}
        otCutoffDate={props.EmployeeSWHSequence?.constance?.HRM_OT_CUTOFF_DATE}
        readOnly={props.readOnly}
        totalEmployee={props.EmployeeSWHSequence?.total_employee}
        totalEmployeeApproved={props.EmployeeSWHSequence?.total_employee_approved}
        totalSentPayrolls={props.EmployeeSWHSequence?.total_sent_payrolls}
        languageUX={props.languageUX}
        divisionOptions={props.divisionOptions}
        monthYearOptions={props.EmployeeSWHSequence?.monthYearOptions}
        onChangeDivision={handleChangeDivision}
        onChangeFilter={handleChangeFilter}
        onPrintReport={handlePrintReport}
        buttonApply={
          <ButtonLoadCheck
            setProp={props.setProp}
            color="blue"
            paramKey={BTN_ACTS.SEARCH}
            size="small"
            title="APPLY"
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SEARCH]}
            onClick={handleSearch}
          />
        }
        historyIcon={
          <Icon
            style={{
              color: "rgba(0, 0, 0, .87)",
              fontSize: "0.75rem",
              margin: "0",
              transform: "rotate(10deg)",
            }}
            name="history"
          />
        }
        table={
          <div className="table-esw" style={{ maxHeight: "460px" }}>
            <Table celled compact structured>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell textAlign="center" width={2} />

                  <TableHeaderCell textAlign="center">Carry-over OT (hr.)</TableHeaderCell>
                  <TableHeaderCell textAlign="center">Carry-over Deficit (hr.)</TableHeaderCell>
                  <TableHeaderCell className="header--highlight-blue" textAlign="center">
                    OT Cut off (hr.)
                  </TableHeaderCell>
                  <TableHeaderCell className="header--highlight-blue" textAlign="center">
                    Sum OT (hr.)
                  </TableHeaderCell>
                  <TableHeaderCell
                    className="header--highlight-blue"
                    style={{ minWidth: "7rem" }}
                    textAlign="center"
                  >
                    OT x1 (hr.)
                  </TableHeaderCell>
                  <TableHeaderCell
                    className="header--highlight-blue"
                    style={{ minWidth: "7rem" }}
                    textAlign="center"
                  >
                    OT x1.5 (hr.)
                  </TableHeaderCell>
                  <TableHeaderCell
                    className="header--highlight-blue"
                    style={{ minWidth: "7rem" }}
                    textAlign="center"
                  >
                    OT x3 (hr.)
                  </TableHeaderCell>
                  <TableHeaderCell style={{ minWidth: "5.25rem" }} textAlign="center">
                    Remaining{"\n"}OT (hr)
                  </TableHeaderCell>
                  <TableHeaderCell style={{ minWidth: "5.25rem" }} textAlign="center">
                    Remaining{"\n"}Deficit (hr)
                  </TableHeaderCell>
                  <TableHeaderCell textAlign="center">No. of late</TableHeaderCell>
                  <TableHeaderCell style={{ minWidth: "7rem" }} textAlign="center">
                    Accumulated{"\n"}Late Time (min.)
                  </TableHeaderCell>
                  <TableHeaderCell textAlign="center">No. of Early out</TableHeaderCell>
                  <TableHeaderCell style={{ minWidth: "8.75rem" }} textAlign="center">
                    Accumulated{"\n"}Early out Time (min.)
                  </TableHeaderCell>
                  <TableHeaderCell textAlign="center">No. of Absent</TableHeaderCell>
                </TableRow>
              </TableHeader>

              <TableBody>
                {formattedItems.length === 0 ? (
                  <TableCell style={{ color: "grey" }} colSpan="15" textAlign="center">
                    No rows found
                  </TableCell>
                ) : null}
                {formattedItems.map((item, index) => (
                  <>
                    <TableRow>
                      <TableCell className="vertical-align-top" rowSpan={2}>
                        {item.employee_full_name}
                        {item.isPayroll && (
                          <div style={{ fontSize: "10px" }}>
                            Send Payroll : {formatISOToThaiDate(item.created_at)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell textAlign="center">{item.carry_over_ot ?? 0}</TableCell>
                      <TableCell textAlign="center">{item.carry_over_deficit}</TableCell>
                      <TableCell textAlign="center">{item.cutoff_over_ot}</TableCell>
                      <TableCell textAlign="center">{item.sum_ot}</TableCell>
                      <TableCell className="--override-disabled --grey">
                        <Input
                          className={item.isMismatch ? "red-text" : ""}
                          name="ot_1hr"
                          readOnly={item.isPayroll ?? props.readOnly}
                          size="small"
                          type="number"
                          value={item.ot_1hr || ""}
                          fluid
                          onChange={handleChangeValue(index)}
                        />
                      </TableCell>
                      <TableCell className="--override-disabled --grey">
                        <Input
                          className={item.isMismatch ? "red-text" : ""}
                          name="ot_1_5hr"
                          readOnly={item.isPayroll ?? props.readOnly}
                          size="small"
                          type="number"
                          value={item.ot_1_5hr || ""}
                          fluid
                          onChange={handleChangeValue(index)}
                        />
                      </TableCell>
                      <TableCell className="--override-disabled --grey">
                        <Input
                          className={item.isMismatch ? "red-text" : ""}
                          name="ot_3hr"
                          readOnly={item.isPayroll ?? props.readOnly}
                          size="small"
                          type="number"
                          value={item.ot_3hr || ""}
                          fluid
                          onChange={handleChangeValue(index)}
                        />
                      </TableCell>
                      <TableCell textAlign="center">{item.remaining_actual_hour}</TableCell>
                      <TableCell textAlign="center">{item.remaining_over_deficit}</TableCell>
                      <TableCell textAlign="center">{item.late_slot_count}</TableCell>
                      <TableCell textAlign="center">{item.total_late_minutes}</TableCell>
                      <TableCell textAlign="center">{item.early_slot_count}</TableCell>
                      <TableCell textAlign="center">{item.total_early_minutes}</TableCell>
                      <TableCell textAlign="center">{item.absent_slot_count}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={14}>
                        <div
                          className="flex-box"
                          style={{ alignItems: "stretch", fontSize: "0.925rem" }}
                        >
                          <div
                            className="flex-box yellow-box"
                            style={{ fontWeight: "bold", marginRight: "0.25rem" }}
                          >
                            Shift code summary (Slot)/ Month
                          </div>

                          {Object.entries({
                            ...item.template_summary,
                            ...item.manual_summary,
                          }).map(([key, value]) => (
                            <div
                              key={`template-summary-${key}`}
                              className="flex-box"
                              style={{
                                color: "rgba(66, 65, 65, 1)",
                                flexDirection: "column",
                                padding: "0rem 1rem 0.15rem",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "0.825rem",
                                  marginTop: "-4px",
                                  padding: "0.15rem 0px 0",
                                }}
                              >
                                {key}
                              </div>
                              <div
                                style={{
                                  backgroundColor: "rgba(233, 249, 251, 1)",
                                  border: "1px solid rgba(93, 188, 210, 1)",
                                  fontSize: "0.8rem",
                                  padding: "0 1rem",
                                }}
                              >
                                {value}
                              </div>
                            </div>
                          ))}

                          <div
                            className="flex-box yellow-box"
                            style={{
                              flexDirection: "column",
                              justifyContent: "center",
                              marginLeft: "0.25rem",
                              textAlign: "center",
                            }}
                          >
                            <div>Total slot</div>
                            <div style={{ fontWeight: "bold" }}>{item.totalSlot}</div>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  </>
                ))}

                {!props.readOnly && formattedItems.length > 0 && (
                  <TableRow>
                    <TableCell />
                    <TableCell colSpan={7} textAlign="center">
                      <ButtonLoadCheck
                        setProp={props.setProp}
                        style={{ minWidth: "10rem" }}
                        color="green"
                        disabled={!enabledSend}
                        paramKey={BTN_ACTS.SEND_PAYROLL}
                        size="small"
                        title="Send to Payroll"
                        buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SEND_PAYROLL]}
                        onClick={handleSendPayroll}
                      />

                      <ButtonLoadCheck
                        setProp={props.setProp}
                        style={{ marginLeft: "5rem", minWidth: "10rem" }}
                        color="blue"
                        disabled={!enabledSaveDraft}
                        paramKey={BTN_ACTS.SAVE_DRAFT}
                        size="small"
                        title="Save Draft"
                        buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SAVE_DRAFT]}
                        onClick={handleSaveDraft}
                      />
                    </TableCell>{" "}
                    <TableCell colSpan={7} textAlign="center">
                      <ButtonLoadCheck
                        setProp={props.setProp}
                        color="blue"
                        paramKey={BTN_ACTS.CALCULATE}
                        size="small"
                        title="Calculate"
                        buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.CALCULATE]}
                        onClick={handleCalculate}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        }
      />
    </div>
  );
};

CardEmployeeSummaryWorkHour.displayName = "CardEmployeeSummaryWorkHour";

export default React.memo(CardEmployeeSummaryWorkHour);
